/**
 * Environment Configuration
 *
 * Centralized configuration for environment-specific settings.
 * This replaces hardcoded values and provides a secure,
 * configurable approach to application settings.
 */

/**
 * Get environment variable with fallback
 * @param {string} key - Environment variable key
 * @param {any} fallback - Fallback value if not found
 * @returns {any} Environment variable value or fallback
 */
const getEnvVar = (key, fallback = null) => {
  const value = import.meta.env[key];
  return value !== undefined ? value : fallback;
};

/**
 * Environment configuration
 */
export const environment = {
  // Environment info
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE,

  // API configuration
  api: {
    baseUrl: getEnvVar("VITE_API_BASE_URL", "http://localhost:3001"),
    timeout: parseInt(getEnvVar("VITE_API_TIMEOUT", "30000")),
    retryAttempts: parseInt(getEnvVar("VITE_API_RETRY_ATTEMPTS", "3")),
    retryDelay: parseInt(getEnvVar("VITE_API_RETRY_DELAY", "1000")),
  },

  // Supabase configuration (no hardcoded fallbacks)
  supabase: {
    url: getEnvVar("VITE_SUPABASE_URL"),
    anonKey: getEnvVar("VITE_SUPABASE_ANON_KEY"),
  },

  // Feature flags
  features: {
    enableGameLaunch: getEnvVar("VITE_ENABLE_GAME_LAUNCH", "true") === "true",
    enableCryptoPayments:
      getEnvVar("VITE_ENABLE_CRYPTO_PAYMENTS", "true") === "true",
  },

  // Logging configuration
  logging: {
    enableConsoleLogging: import.meta.env.DEV,
    enableApiLogging: getEnvVar("VITE_ENABLE_API_LOGGING", "true") === "true",
    enableErrorReporting:
      getEnvVar("VITE_ENABLE_ERROR_REPORTING", "false") === "true",
    logLevel: getEnvVar("VITE_LOG_LEVEL", "info"),
  },

  // Security configuration
  security: {
    enableAuthLogging: import.meta.env.DEV,
    sessionTimeout: parseInt(getEnvVar("VITE_SESSION_TIMEOUT", "86400000")), // 24 hours
    requireEmailVerification:
      getEnvVar("VITE_REQUIRE_EMAIL_VERIFICATION", "true") === "true",
    enableTwoFactor: getEnvVar("VITE_ENABLE_TWO_FACTOR", "false") === "true",
  },

  // UI configuration
  ui: {
    theme: getEnvVar("VITE_DEFAULT_THEME", "dark"),
    enableAnimations: getEnvVar("VITE_ENABLE_ANIMATIONS", "true") === "true",
    enableSounds: getEnvVar("VITE_ENABLE_SOUNDS", "false") === "true",
    autoScrollInterval: parseInt(
      getEnvVar("VITE_AUTO_SCROLL_INTERVAL", "4000"),
    ),
  },

  // Game configuration
  games: {
    enableHub88: getEnvVar("VITE_ENABLE_HUB88", "true") === "true",
    defaultCurrency: getEnvVar("VITE_DEFAULT_CURRENCY", "GLD"),
    enableDemoMode: getEnvVar("VITE_ENABLE_DEMO_MODE", "false") === "true",
  },

  // Admin configuration
  admin: {
    emails: getEnvVar("VITE_ADMIN_EMAILS", "david@gladwin.tech")
      .split(",")
      .map((email) => email.trim()),
    redirectPath: getEnvVar("VITE_ADMIN_REDIRECT_PATH", "/admin"),
    sessionTimeout: parseInt(
      getEnvVar("VITE_ADMIN_SESSION_TIMEOUT", "86400000"),
    ), // 24 hours
  },
};

/**
 * Get configuration value by path
 * @param {string} path - Dot-separated path to config value
 * @param {any} fallback - Fallback value if not found
 * @returns {any} Configuration value or fallback
 */
export const getConfig = (path, fallback = null) => {
  const keys = path.split(".");
  let value = environment;

  for (const key of keys) {
    if (value && typeof value === "object" && key in value) {
      value = value[key];
    } else {
      return fallback;
    }
  }

  return value;
};

/**
 * Check if a feature is enabled
 * @param {string} featureName - Feature name
 * @returns {boolean} True if feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  return getConfig(`features.${featureName}`, false);
};

/**
 * Get API configuration
 * @returns {Object} API configuration
 */
export const getApiConfig = () => {
  return environment.api;
};

/**
 * Get Supabase configuration
 * @returns {Object} Supabase configuration
 */
export const getSupabaseConfig = () => {
  return environment.supabase;
};

/**
 * Check if logging is enabled for a specific type
 * @param {string} logType - Type of logging (console, api, error)
 * @returns {boolean} True if logging is enabled
 */
export const isLoggingEnabled = (logType = "console") => {
  const key = `enable${logType.charAt(0).toUpperCase() + logType.slice(1)}Logging`;
  return getConfig(`logging.${key}`, false);
};

/**
 * Validate environment configuration
 * @returns {Object} Validation result
 */
export const validateEnvironment = () => {
  const errors = [];
  const warnings = [];

  // Check required environment variables
  if (!environment.supabase.url) {
    errors.push("VITE_SUPABASE_URL is required");
  }

  if (!environment.supabase.anonKey) {
    errors.push("VITE_SUPABASE_ANON_KEY is required");
  }

  // Check for development-only settings in production
  if (environment.isProduction) {
    if (environment.logging.enableConsoleLogging) {
      warnings.push("Console logging is enabled in production");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export default environment;
